import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import {
  settingsChangeHistoryQuery,
  type HistoryFilters,
  type HistoryRow,
} from '../../api/settingsEaseedeskHistory'
import { LoadFailed } from '../../ui/LoadFailed'
import { SettingsHead } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Change history.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * change_history/index.php`: trei filtre pe un rand, tabelul, si o paginare
 * "Newer / Older" - jurnalul se citeste de la cel mai nou incolo.
 *
 * Denumirile in locul ID-urilor si cuvintele dinaintea valorilor - "from",
 * "to", "added", "cleared, was" - vin de la server (`EaseedeskSettingsHistory`),
 * ca in portal. Aici doar se scriu.
 */
function labelOf(value: string) {
  const words = value.replace(/_/g, ' ').trim()

  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** O valoare, cu cuvantul dinaintea ei. O culoare se vede, nu se citeste. */
function Said({ word, label, color }: { word: string; label: string; color: string | null }) {
  return (
    <>
      {word ? <span className="color-light-grey">{word} </span> : null}
      {color ? (
        <span
          style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: 2,
            border: '1px solid rgba(0, 0, 0, .2)',
            background: color,
          }}
        />
      ) : null}
      {color ? ' ' : null}
      {label}
    </>
  )
}

function Change({ row }: { row: HistoryRow }) {
  return (
    <>
      <span className="label label-default">{row.action_label}</span> {row.entity_label}
      {row.field_label ? (
        <>
          {' · '}
          <strong>{row.field_label}</strong>{' '}
          {row.old_label !== null ? <Said word={row.old_word} label={row.old_label} color={row.old_color} /> : null}{' '}
          {row.new_label !== null ? <Said word={row.new_word} label={row.new_label} color={row.new_color} /> : null}
        </>
      ) : null}
    </>
  )
}

export function SettingsChangeHistoryPage() {
  const [filters, setFilters] = useState<HistoryFilters>({ ticket_type: 0, entity_type: '', user_id: 0 })
  const [page, setPage] = useState(1)

  const query = useQuery(settingsChangeHistoryQuery(filters, page))

  /* Orice filtru schimbat duce inapoi la prima pagina: pagina 4 a unei liste
     si pagina 4 a alteia n-au nimic de-a face una cu alta. */
  const change = (next: Partial<HistoryFilters>) => {
    setFilters((current) => ({ ...current, ...next }))
    setPage(1)
  }

  if (query.error instanceof ApiError && query.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (query.error) {
    return <LoadFailed what="The change history" error={query.error} onRetry={() => void query.refetch()} />
  }
  if (!query.data) return <Spinner />

  const data = query.data
  const pages = Math.ceil(data.total / Math.max(1, data.per_page))

  return (
    <div>
      <SettingsHead
        strong="Change"
        rest="history"
        subtitle="Who changed what in the ticket settings, and when"
      />

      {!data.available ? (
        <div className="alert alert-info">
          <i className="fas fa-info-circle" aria-hidden="true"></i> Nothing has been recorded yet. The journal starts
          filling in as soon as someone changes a ticket setting.
        </div>
      ) : null}

      <form className="form-inline" style={{ marginBottom: 14 }} onSubmit={(e) => e.preventDefault()}>
        <label style={{ marginRight: 6 }}>Ticket type</label>
        <select
          className="form-control input-sm"
          style={{ marginRight: 12 }}
          value={filters.ticket_type}
          onChange={(e) => change({ ticket_type: Number(e.target.value) })}
        >
          <option value={0}>any</option>
          {data.types.map((type) => (
            <option key={type.ID} value={type.ID}>
              {type.type_name}
            </option>
          ))}
        </select>

        <label style={{ marginRight: 6 }}>Setting</label>
        <select
          className="form-control input-sm"
          style={{ marginRight: 12 }}
          value={filters.entity_type}
          onChange={(e) => change({ entity_type: e.target.value })}
        >
          <option value="">any</option>
          {data.entity_types.map((entity) => (
            <option key={entity} value={entity}>
              {labelOf(entity)}
            </option>
          ))}
        </select>

        <label style={{ marginRight: 6 }}>Who</label>
        <select
          className="form-control input-sm"
          style={{ marginRight: 12 }}
          value={filters.user_id}
          onChange={(e) => change({ user_id: Number(e.target.value) })}
        >
          <option value={0}>anyone</option>
          {data.people.map((person) => (
            <option key={person.user_id} value={person.user_id}>
              {person.user_name || `user #${person.user_id}`}
            </option>
          ))}
        </select>

        <span className="color-light-grey" style={{ fontSize: 12 }}>
          {data.total} change(s)
        </span>
      </form>

      <table className="table table-hover dataTable">
        <thead>
          <tr>
            <th className="align-left" style={{ width: 150 }}>
              When
            </th>
            <th className="align-left" style={{ width: 160 }}>
              Who
            </th>
            <th className="align-left" style={{ width: 150 }}>
              Ticket type
            </th>
            <th className="align-left">What changed</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <tr key={row.ID}>
              <td className="color-light-grey">{row.created_at}</td>
              <td>{row.user_name || `user #${row.user_id}`}</td>
              <td>
                {row.type_name ? (
                  row.type_name
                ) : (
                  <span className="color-light-grey" title="A setting that is shared, not tied to one ticket type">
                    &mdash;
                  </span>
                )}
              </td>
              <td>
                <Change row={row} />
              </td>
            </tr>
          ))}
          {data.rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="color-light-grey">
                Nothing matches these filters.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {pages > 1 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            className="btn btn-default btn-sm"
            disabled={data.page <= 1}
            onClick={() => setPage(Math.max(1, data.page - 1))}
          >
            <i className="fas fa-arrow-left" aria-hidden="true"></i> Newer
          </button>

          <span className="color-light-grey" style={{ fontSize: 12 }}>
            page {data.page} of {pages}
          </span>

          <button
            type="button"
            className="btn btn-default btn-sm"
            disabled={data.page >= pages}
            onClick={() => setPage(Math.min(pages, data.page + 1))}
          >
            Older <i className="fas fa-arrow-right" aria-hidden="true"></i>
          </button>
        </div>
      ) : null}
    </div>
  )
}
